import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Download } from 'lucide-react';
import { downloadPdf } from '../../utils/downloadPdf';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminQuotes() {
  const { t } = useLanguage();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const params = filter ? `?status=${filter}` : '';
    axios.get(`${API}/api/admin/quotes${params}`, { withCredentials: true }).then(r => setQuotes(r.data)).catch(() => {});
  }, [filter]);

  const updateStatus = async (id: string, status: string) => {
    await axios.put(`${API}/api/admin/quotes/${id}`, { status }, { withCredentials: true });
    setQuotes(quotes.map(q => q._id === id ? { ...q, status } : q));
  };

  const statuses = ['', 'pending', 'approved', 'rejected'];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="quotes" />
      <main data-testid="admin-quotes-page" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>{t('admin.quotes')}</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              {statuses.map(s => (
                <button key={s} data-testid={`filter-${s || 'all'}`} onClick={() => setFilter(s)}
                  style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: 600, border: '1px solid var(--border)',
                    background: filter === s ? 'var(--primary)' : 'white',
                    color: filter === s ? 'white' : 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer',
                  }}>
                  {s ? t(`status.${s}`) : 'All'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {[t('quote.company'), t('quote.product'), t('quote.category'), 'Qty', t('quote.email'), t('portal.status'), t('portal.date'), 'PDF', t('portal.actions')].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(q => (
                    <tr key={q._id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 16px', fontSize: 14 }}>{q.company_name || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>{q.product_name}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{q.product_category}</td>
                      <td style={{ padding: '12px 16px', fontSize: 14 }}>{q.quantity}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{q.contact_email}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 8px', fontSize: 11, fontWeight: 600, background: q.status === 'approved' ? 'rgba(34,197,94,0.12)' : q.status === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.12)', color: q.status === 'approved' ? '#16A34A' : q.status === 'rejected' ? '#DC2626' : '#D97706', textTransform: 'uppercase' }}>
                          {t(`status.${q.status}`)}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{new Date(q.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button onClick={() => downloadPdf(`/api/quotes/${q._id}/pdf`, `quote-${q._id.slice(-8)}.pdf`)} data-testid={`admin-quote-pdf-${q._id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, background: 'rgba(13,71,161,0.08)', color: 'var(--primary)', border: '1px solid rgba(13,71,161,0.2)', cursor: 'pointer' }}>
                          <Download size={10} /> PDF
                        </button>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {q.status !== 'approved' && (
                            <button data-testid={`approve-${q._id}`} onClick={() => updateStatus(q._id, 'approved')}
                              style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'rgba(34,197,94,0.12)', color: '#16A34A', border: '1px solid rgba(34,197,94,0.3)', cursor: 'pointer' }}>
                              Approve
                            </button>
                          )}
                          {q.status !== 'rejected' && (
                            <button data-testid={`reject-${q._id}`} onClick={() => updateStatus(q._id, 'rejected')}
                              style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'rgba(239,68,68,0.12)', color: '#DC2626', border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer' }}>
                              Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {quotes.length === 0 && (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>{t('portal.no_quotes')}</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
